import { defineStore } from 'pinia'
import {
  createDefaultCheckoutLayoutConfig,
  DEFAULT_PAGE_META,
  mergePageMeta,
  isSectionEffectivelyVisible,
  LEFT_COLUMN_SECTION_IDS,
  mergeSectionContent,
  clampDropInsertIndex,
  getDragBlockRange,
  isNoOpBlockMove,
  moveSectionsBlock,
  isSectionOrderBroken,
  normalizeCheckoutLayoutConfig,
  normalizeFunnelType,
  parseDiscountConfigFromFunnelTemplate,
  parseDiscountConfigFromSections,
  mergeDiscountIntoSections,
  discountConfigToLegacyOptions,
  DEFAULT_DISCOUNT_CODE_CONFIG,
  sortSectionsCanonical,
  type CheckoutFunnelType,
  type CheckoutLayoutConfig,
  type CheckoutLayoutSection,
  type CheckoutSectionId,
  type DiscountCodeConfig,
  type SectionContent,
} from '@/config/checkoutLayout'
import {
  normalizeCustomShippingMethods,
  parseCustomShippingMethods,
} from '@/utils/shippingMethodConfig'
import {
  checkoutThemePresets,
  getPresetTheme,
  sanitizeThemeConfig,
  THEME_NUMBER_KEYS,
  type CheckoutTemplateConfig,
  type CheckoutThemePresetId,
} from '@/config/checkoutTheme'

type CheckoutLayoutState = CheckoutLayoutConfig & {
  templateId: string | number
  templateName: string
  funnelType: CheckoutFunnelType
  theme: CheckoutTemplateConfig
  themePresetId: CheckoutThemePresetId
  meta: Record<string, string>
  // Legacy flat template.options (colors, discountCodeDisplay, ...). Preserved on
  // save so the layout editor does not wipe unrelated keys from older editors.
  templateOptions: Record<string, unknown>
  // Structured discount config (Show Discount Code toggle in Order Summary panel).
  discount: DiscountCodeConfig
  // Funnel-level setting (instant_capture / 3DS / locale ...). Edited from the
  // Base panel and persisted through updateFunnel, not the template endpoint.
  funnelSetting: Record<string, any>
  // Serialized snapshot of funnelSetting at load time, used to detect whether
  // the Base panel settings actually changed before firing updateFunnel.
  funnelSettingBaseline: string
}

const THEME_NUMBER_KEY_SET = new Set<string>(THEME_NUMBER_KEYS as string[])

function applyDiscountFromSections(state: CheckoutLayoutState) {
  state.discount = parseDiscountConfigFromSections(state.sections)
}

function createInitialState(): CheckoutLayoutState {
  return {
    ...createDefaultCheckoutLayoutConfig(),
    templateId: 'default',
    templateName: '',
    funnelType: 'one_page_checkout',
    theme: getPresetTheme('defaultLight'),
    themePresetId: 'defaultLight',
    meta: { ...DEFAULT_PAGE_META },
    templateOptions: {},
    discount: { ...DEFAULT_DISCOUNT_CODE_CONFIG },
    funnelSetting: {},
    funnelSettingBaseline: stableStringify({}),
  }
}

/** Stable JSON stringify (sorted keys) so key order doesn't cause false diffs. */
function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.keys(val)
        .sort()
        .reduce<Record<string, unknown>>((acc, k) => {
          acc[k] = (val as Record<string, unknown>)[k]
          return acc
        }, {})
    }
    return val
  })
}

export const useCheckoutLayoutStore = defineStore('checkoutLayout', {
  state: (): CheckoutLayoutState => createInitialState(),
  getters: {
    isThreePageCheckout(state): boolean {
      return state.funnelType === 'three_pages_checkout'
    },
    // True only when the Base panel funnel settings differ from what was loaded,
    // so the save flow can skip the extra updateFunnel call when nothing changed.
    isFunnelSettingDirty(state): boolean {
      return stableStringify(state.funnelSetting) !== state.funnelSettingBaseline
    },
    visibleLeftSectionIds(state): CheckoutSectionId[] {
      return state.sections
        .filter(
          (s) =>
            LEFT_COLUMN_SECTION_IDS.includes(s.id) &&
            isSectionEffectivelyVisible(state.sections, s.id),
        )
        .map((s) => s.id)
    },
    isSectionVisible:
      (state) =>
      (id: CheckoutSectionId): boolean =>
        isSectionEffectivelyVisible(state.sections, id),
    sectionById:
      (state) =>
      (id: CheckoutSectionId): CheckoutLayoutSection | undefined =>
        state.sections.find((s) => s.id === id),
    sectionContent:
      (state) =>
      (id: CheckoutSectionId): SectionContent => {
        const section = state.sections.find((s) => s.id === id)
        return mergeSectionContent(id, section?.content)
      },
  },
  actions: {
    setFunnelType(funnelType: unknown) {
      this.funnelType = normalizeFunnelType(funnelType)
    },
    setSections(sections: CheckoutLayoutSection[]) {
      const normalized = normalizeCheckoutLayoutConfig({ sections })
      if (normalized) this.sections = normalized.sections
      applyDiscountFromSections(this)
    },
    applyFromJson(raw: unknown): boolean {
      let payload: unknown = raw
      if (payload && typeof payload === 'object' && 'layout' in payload) {
        payload = (payload as { layout: unknown }).layout
      }
      if (Array.isArray(payload)) {
        payload = { sections: payload }
      }
      const normalized = normalizeCheckoutLayoutConfig(payload)
      if (!normalized) return false
      this.sections = normalized.sections
      applyDiscountFromSections(this)
      return true
    },
    applyFromTemplateConfig(templateConfig: Record<string, unknown> | undefined) {
      const fromApi = templateConfig?.layout
      const normalized = normalizeCheckoutLayoutConfig(fromApi)
      if (normalized) {
        const legacyDiscount = parseDiscountConfigFromFunnelTemplate(
          (templateConfig ?? {}) as Record<string, unknown>,
        )
        const rawSections = Array.isArray((fromApi as { sections?: unknown })?.sections)
          ? ((fromApi as { sections: { id?: string }[] }).sections ?? [])
          : []
        const layoutHasDiscountSection = rawSections.some(
          (item) => String(item?.id ?? '') === 'discountCode',
        )
        this.sections = layoutHasDiscountSection
          ? normalized.sections
          : mergeDiscountIntoSections(normalized.sections, legacyDiscount)
        applyDiscountFromSections(this)
      }
    },
    /** Load layout + identity from a wencart funnel detail object. */
    loadFromFunnel(funnel: any, funnelId?: string | number) {
      const template = funnel?.template ?? {}
      // The save endpoint is /funnels/{funnelId}/template, so the id must be the
      // real funnel id (route param), not the placeholder template id.
      this.templateId = funnelId ?? funnel?.id ?? template.id ?? 'default'
      this.templateName = template.name ?? funnel?.templateType ?? ''
      // Funnel type is chosen at creation (one_page_checkout / three_pages_checkout)
      // and drives the stepped layout in the storefront preview.
      this.funnelType = normalizeFunnelType(funnel?.templateType ?? template.name)

      const config =
        template.config && typeof template.config === 'object'
          ? (template.config as Record<string, unknown>)
          : undefined

      const source = template.layout ?? config?.layout
      const normalized = normalizeCheckoutLayoutConfig(source)
      const defaultSections = createDefaultCheckoutLayoutConfig().sections
      let sections = normalized ? normalized.sections : defaultSections

      const legacyDiscount = parseDiscountConfigFromFunnelTemplate(template)
      const rawSections = Array.isArray((source as { sections?: unknown })?.sections)
        ? ((source as { sections: { id?: string }[] }).sections ?? [])
        : []
      const layoutHasDiscountSection = rawSections.some(
        (item) => String(item?.id ?? '') === 'discountCode',
      )

      this.sections = layoutHasDiscountSection
        ? sections
        : mergeDiscountIntoSections(sections, legacyDiscount)

      this.loadTheme(
        template.themePreset ?? config?.themePreset,
        template.theme ?? config?.theme,
      )

      this.meta = mergePageMeta(
        (template.meta ?? config?.meta) as Record<string, string> | undefined,
      )

      // Keep legacy flat options so unrelated keys survive layout-only saves.
      const options = (template.options ??
        (config as Record<string, unknown>)?.options) as
        | Record<string, unknown>
        | undefined
      this.templateOptions =
        options && typeof options === 'object' ? { ...options } : {}

      applyDiscountFromSections(this)

      // Keep the full funnel setting object so unrelated keys (shipping rates,
      // tipping ...) are preserved when we save the Base panel edits back.
      this.funnelSetting =
        funnel?.setting && typeof funnel.setting === 'object'
          ? { ...funnel.setting }
          : {}
      this.funnelSettingBaseline = stableStringify(this.funnelSetting)
    },
    /** Restore theme selection: preset wins (authoritative), else custom theme object. */
    loadTheme(presetId: unknown, themeObject: unknown) {
      const presetKey = typeof presetId === 'string' ? presetId : ''
      if (presetKey && presetKey !== 'custom' && presetKey in checkoutThemePresets) {
        this.themePresetId = presetKey as CheckoutThemePresetId
        this.theme = getPresetTheme(presetKey as keyof typeof checkoutThemePresets)
        return
      }
      const sanitized = sanitizeThemeConfig(themeObject)
      if (Object.keys(sanitized).length > 0) {
        this.themePresetId = 'custom'
        this.theme = sanitized
        return
      }
      this.themePresetId = 'defaultLight'
      this.theme = getPresetTheme('defaultLight')
    },
    /** Select a preset (hardcoded, not editable) or switch to editable custom. */
    setThemePreset(id: CheckoutThemePresetId) {
      if (id === 'custom') {
        // Keep the current theme as the editable starting point.
        this.themePresetId = 'custom'
        return
      }
      if (!(id in checkoutThemePresets)) return
      this.themePresetId = id
      this.theme = getPresetTheme(id as keyof typeof checkoutThemePresets)
    },
    /** Edit one theme field. Only allowed while in custom mode. */
    setThemeField(key: keyof CheckoutTemplateConfig, value: string | number) {
      if (this.themePresetId !== 'custom') return
      const next: CheckoutTemplateConfig = { ...this.theme }
      if (THEME_NUMBER_KEY_SET.has(key as string)) {
        const n = Number(value)
        if (Number.isFinite(n)) next[key] = n as never
        else delete next[key]
      } else {
        const str = String(value).trim()
        if (str) next[key] = str as never
        else delete next[key]
      }
      this.theme = next
    },
    setMetaField(key: string, value: string) {
      this.meta = { ...mergePageMeta(this.meta), [key]: value }
    },
    setSettingField(key: string, value: unknown) {
      this.funnelSetting = { ...this.funnelSetting, [key]: value }
    },
    /** Re-baseline funnelSetting after a successful updateFunnel save. */
    markFunnelSettingSaved() {
      this.funnelSettingBaseline = stableStringify(this.funnelSetting)
    },
    setVisible(id: CheckoutSectionId, visible: boolean) {
      const item = this.sections.find((s) => s.id === id)
      if (item) item.visible = visible
      applyDiscountFromSections(this)
    },
    setSectionContent(id: CheckoutSectionId, key: string, value: unknown) {
      const item = this.sections.find((s) => s.id === id)
      if (!item) return
      item.content = {
        ...mergeSectionContent(id, item.content),
        [key]: value,
      }
      applyDiscountFromSections(this)
    },
    patchSectionContent(
      id: CheckoutSectionId,
      patch: Record<string, unknown>,
      removeKeys: string[] = [],
    ) {
      const item = this.sections.find((s) => s.id === id)
      if (!item) return
      const next = { ...mergeSectionContent(id, item.content), ...patch }
      for (const key of removeKeys) delete next[key]
      item.content = next
      applyDiscountFromSections(this)
    },
    moveSectionsAt(fromIndex: number, insertAt: number) {
      if (fromIndex < 0 || fromIndex >= this.sections.length) return

      const dragId = this.sections[fromIndex]?.id
      if (!dragId) return

      const block = getDragBlockRange(this.sections, fromIndex)
      const clampedInsert = clampDropInsertIndex(
        this.sections,
        dragId,
        insertAt,
        fromIndex,
      )

      if (block.length > 1) {
        if (isNoOpBlockMove(block, clampedInsert)) return
        this.sections = moveSectionsBlock(
          this.sections,
          block.start,
          block.end,
          clampedInsert,
        )
        return
      }

      let targetIndex = clampedInsert
      if (fromIndex < clampedInsert) targetIndex = clampedInsert - 1
      if (targetIndex === fromIndex) return
      if (targetIndex < 0 || targetIndex >= this.sections.length) return

      const next = [...this.sections]
      const [item] = next.splice(fromIndex, 1)
      if (!item) return
      next.splice(targetIndex, 0, item)
      this.sections = next
    },
    repairSectionOrderIfNeeded() {
      if (isSectionOrderBroken(this.sections)) {
        this.sections = sortSectionsCanonical(this.sections)
      }
    },
    /** Re-merge persisted / API sections so newly added blocks (e.g. expressCheckout) appear */
    ensureCanonicalSections() {
      const normalized = normalizeCheckoutLayoutConfig({ sections: this.sections })
      if (normalized) this.sections = normalized.sections
      applyDiscountFromSections(this)
    },
    resetToDefault() {
      this.sections = createDefaultCheckoutLayoutConfig().sections
      this.themePresetId = 'defaultLight'
      this.theme = getPresetTheme('defaultLight')
      this.meta = { ...DEFAULT_PAGE_META }
      applyDiscountFromSections(this)
    },
    serializeSections(): CheckoutLayoutSection[] {
      return this.sections.map((s) => ({
        id: s.id,
        label: s.label,
        visible: s.visible,
        content: { ...s.content },
      }))
    },
    /**
     * Sections shaped for persistence. Custom shipping methods are stored in the
     * editor as a JSON string, but the save payload sends them as a real JSON
     * object/array instead of an escaped string.
     */
    serializeSectionsForSave(): Array<Record<string, unknown>> {
      return this.sections.map((s) => {
        const content: Record<string, unknown> = { ...s.content }
        if (s.id === 'shippingMethod' && 'customShippingMethods' in content) {
          content.customShippingMethods = normalizeCustomShippingMethods(
            parseCustomShippingMethods(content.customShippingMethods),
          )
        }
        return {
          id: s.id,
          label: s.label,
          visible: s.visible,
          content,
        }
      })
    },
    toTemplateConfigFragment(): { layout: CheckoutLayoutConfig } {
      return { layout: { sections: this.serializeSections() } }
    },
    setDiscountConfig(config: DiscountCodeConfig) {
      this.sections = mergeDiscountIntoSections(this.sections, config)
      applyDiscountFromSections(this)
    },
    setDiscountField<K extends keyof DiscountCodeConfig>(
      key: K,
      value: DiscountCodeConfig[K],
    ) {
      this.setDiscountConfig({ ...this.discount, [key]: value })
    },
    /** templateConfig pushed to the remote preview: { checkout: { layout, theme, funnelType } }. */
    toPreviewTemplateConfig(): Record<string, unknown> {
      const discount = parseDiscountConfigFromSections(this.sections)
      return {
        checkout: {
          funnelType: this.funnelType,
          layout: { sections: this.serializeSectionsForSave() },
          theme: { ...this.theme },
          meta: { ...this.meta },
          discount: { ...discount },
        },
      }
    },
    /** Full funnel template payload for updateFunnelTemplate(). */
    toFunnelTemplatePayload() {
      const discount = parseDiscountConfigFromSections(this.sections)
      const legacyDiscount = discountConfigToLegacyOptions(discount)
      return {
        funnel: {
          template: {
            id: this.templateId,
            name: this.templateName,
            funnelType: this.funnelType,
            layout: { sections: this.serializeSectionsForSave() },
            theme: { ...this.theme },
            themePreset: this.themePresetId,
            meta: { ...this.meta },
            discount: { ...discount },
            options: {
              ...this.templateOptions,
              ...legacyDiscount,
            },
          },
        },
      }
    },
  },
})
